import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'
import { PrismicRichText } from '@prismicio/react'
import { useLocation } from '@reach/router'
import { DesignRow } from 'components/design'

const GlobalIncludes = ({ incomingSeoFields }) => {
	useScrollbarWidth()

	// pass Scheduled event from Calendly to GTM & Analytics
	useCalendlyEvent()

	const { title, description, canonicalUrl, bottom_text } =
		useMetaData(incomingSeoFields)

	return (
		<>
			<Helmet>
				<html lang="en-US" />
				<title>{title}</title>
				<meta name="description" content={description || ''} />
				<link rel="canonical" href={canonicalUrl} />
				<link
					rel="preload"
					as="font"
					href="/assets/fonts/TTFirsNeue-DemiBold.woff2"
					crossorigin="anonymous"
				/>
				{/* Defer loading Calendly CSS until Page Load */}
				<link
					rel="preload"
					href="https://assets.calendly.com/assets/external/widget.css"
					as="style"
					onload="this.onload=null;this.rel='stylesheet'"
				/>
				<script
					src="https://assets.calendly.com/assets/external/widget.js"
					type="text/javascript"
					async
				/>
				{/* Pinterest verification tag */}
				<meta name="p:domain_verify" content="eb23001403234e78bcd458ea2502853f" />
			</Helmet>

			{bottom_text?.richText.length ? (
				<DesignRow
					className="mt-16"
					columnToColumn={
						<div className="bottom-text">
							<PrismicRichText field={bottom_text.richText} />
						</div>
					}
				/>
			) : null}
		</>
	)
}

/* In order to calculate the real visible width in CSS we have to subtract
 * the scrollbar width from the viewport width reported by the browser.
 * The following effect overrides the CSS variable with the actual value
 * on every page navigation.
 */
const useScrollbarWidth = () => {
	useEffect(() => {
		document.documentElement.style.setProperty(
			'--scrollbar-width',
			window.innerWidth - document.documentElement.clientWidth + 'px'
		)
	})
}

const useMetaData = (incomingSeoFields) => {
	const data = useStaticQuery(graphql`
		query seoQuery {
			prismicSeoFieldsForPages {
				data {
					pages {
						bottom_text {
							richText
						}
						description
						pathname
						title
					}
				}
			}
			site {
				siteMetadata {
					title
					siteUrl
				}
			}
		}
	`)

	const seoFieldsDict = {}
	data.prismicSeoFieldsForPages.data.pages.forEach((page) => {
		const pathname = page.pathname ? `/${page.pathname}/` : '/'
		seoFieldsDict[pathname] = {
			title: page.title,
			description: page.description,
			bottom_text: page.bottom_text,
		}
	})
	const location = useLocation()
	// Pagination number pages force the first one as canonical
	const pathname =
		incomingSeoFields && incomingSeoFields.hasOwnProperty('canonicalPathname')
			? incomingSeoFields.canonicalPathname
			: location.pathname
	const metadata = seoFieldsDict.hasOwnProperty(pathname)
		? seoFieldsDict[pathname]
		: {
				title: data.site.siteMetadata.title,
				description: null,
				bottom_text: null,
		  }
	metadata.canonicalUrl = data.site.siteMetadata.siteUrl + pathname

	// Repeatable types (namely BlogPost and Project) may pass fields to override from their own document
	if (incomingSeoFields) {
		if (incomingSeoFields.hasOwnProperty('title')) {
			metadata.title = incomingSeoFields.title
		}
		if (incomingSeoFields.hasOwnProperty('description')) {
			metadata.description = incomingSeoFields.description
		}
		if (incomingSeoFields.hasOwnProperty('bottom_text')) {
			metadata.bottom_text = incomingSeoFields.bottom_text
		}
	}

	return metadata
}

const useCalendlyEvent = () => {
	useEffect(() => {
		const dispatchCalendlyEvent = (e) => {
			if (e.data.event && e.data.event === 'calendly.event_scheduled') {
				window.dataLayer &&
					window.dataLayer.push({ event: 'calendly.event_scheduled' })
			}
		}

		window.addEventListener('message', dispatchCalendlyEvent)

		return () => {
			window.removeEventListener('message', dispatchCalendlyEvent)
		}
	}, [])
}

export default GlobalIncludes
